<script>
    import { onMount } from "svelte";
    import Loader from '../../Loader.svelte';
    import SortableTable from '../../SortableTable.svelte';
    import Library from "../../../Models/libraryORM";
    import {notifier} from '@beyonk/svelte-notifications';
    import {addFormModal} from '../../../Forms/ModalFormManager.svelte';
    import {confirm} from '../../Confirmation.svelte';
    import Collapse from '../../Collapse.svelte';

    import {session} from '../../../Utilities/Session.svelte'
    import TagView from "../../TagView.svelte";
    import TagEditor from '../../../Forms/CustomFields/TagEditor.svelte';

    import AssessmentLibraryNotes from './AssessmentLibraryNotes.svelte';
    import parseFields from '../../../Utilities/parseFields';

    import Fa from 'svelte-fa';
    import {faComment} from "@fortawesome/free-solid-svg-icons";


    export let section;
    export let assessment;
    export let assessment_model;

    let library_model;
    let library;
    let library_unsub;

    let modal;

    let sortFields;
    let formFields;

    let library_items;
    let library_tags;

    let mapped_items = [];

    // We know that since we are doing a simple 1:1 mapping from controls to Library Items
    // if two controls map to the same item, we simply pick one of them
    // this should be sufficient for most cases.
    let control_mappings = {};

    let libraryFieldData;

    function load_library_data(section) {
        if (library_unsub) {
            // unsubscribe from previous library
            library_unsub();
            library_unsub = undefined;
        }

        library_model = new Library(section.library_type);

        library_unsub = library_model.store.subscribe((lib) => {
            if (!lib) return;

            library = lib;

            libraryFieldData = parseFields(lib.fields);

            formFields = [...libraryFieldData.formFields];
            sortFields = [...libraryFieldData.sortFields];

            if (section.extra_fields) {
                let extraFieldData = parseFields(section.extra_fields, false);
                sortFields.push(...extraFieldData.sortFields);
                formFields.push(...extraFieldData.formFields);
            }

            let tags = [];
            if (section.tags && section.tags.length) {
                tags = [...section.tags];
            }

            if (library_tags.tags.length) {
                tags = [...tags, ...library_tags.tags];
            }

            // handle tag field
            if (tags.length) {
                function filterFunc (filter, item) {
                    if (!filter) return true;

                    let inverse = filter.startsWith("-");

                    if (!item["tags_"]) return inverse;

                    if (inverse) filter = filter.slice(1);

                    console.log(filter);

                    for (let tag of item["tags_"]) {
                        if (tag.name.toLowerCase().includes(filter.toLowerCase())) {
                            return !inverse;
                        }
                    }
                    return inverse;
                }

                formFields.push({name: "tags_", label: "Tags", type:"customTags", options: [...tags]})
                sortFields.push({field: "tags_", label: "Tags", "component": TagView, type: "customTags", filterFunc});
            }
        });
    }

    function get_item(item_id) {
        for (let item of library.items) {
            if (item.details._id == item_id) {
                return item.details;
            }
        }
    }

    function load_mapped_items(assessment, library) {
        if (!(assessment && library)) return;

        control_mappings = {};
        let mappings = new Set();
        assessment.assessment_type_data.categories.forEach((category) => {
            category.subcategories.forEach((subcategory) => {
                subcategory.controls.forEach((control) => {
                    let control_answer = assessment_model.getResult(control.id, '', assessment);

                    if (control_answer) {
                        let recommendation_map = control.recommendation_map[control_answer.value];

                        if (recommendation_map){
                            recommendation_map.forEach((mapping) => {
                                if (mapping.section_id == section.section_id) {
                                    mappings.add(mapping.id);
                                    control_mappings[mapping.id] = control.id;
                                }
                            });
                        }
                    }


                    for (let [tag_id, mapping] of Object.entries(control.tag_mapping)) {
                        if (assessment_model.getTag(control.id, tag_id).value == 1) {
                            for (let map of mapping) {
                                if (map.section_id == section.section_id) {
                                    mappings.add(map.id);
                                    control_mappings[mapping.id] = control.id;
                                }
                            }
                        }
                    }
                });
            });
        });

        mapped_items = [];

        mappings.forEach((id) => {
            let item = get_item(id);
            if (item) {
                mapped_items.push(item);
            }
        });

        mapped_items = mapped_items;
    }

    function load_section_data(assessment, section) {
        library_items = assessment.report_data[section.section_name] || [];
        library_tags = assessment.library_tags[section.section_name] || {tags: []}
    }

    function editItem(item) {
        addFormModal({
            fields: formFields,
            data: item
        }).then(formData => {
            assessment_model.saveLibraryItem(section, formData).then(() => {
                notifier.success("Item Updated");
            });
        }).catch((err) => {
            if (err !== "cancel"){
                notifier.danger("Error saving Item");
            }
        });
    }

    function deleteItem(item) {
        confirm("Are you sure you want to remove this item from the report?").then(() => {
            assessment_model.deleteLibraryItem(item).then(() => {
                notifier.success("Item removed");
            }).catch(() => null);
        }).catch(() => null);
    }

    function addItemFromTemplate() {
        return new Promise((resolve, reject) => {
            modal = {
                cancelAction: reject,
                saveAction: resolve
            }
        }).then(addTemplatedItem).catch((err) => {
            console.info(err);
        }).finally(() => {
            modal = undefined;
        })
    }

    function addTemplatedItem(item) {
        // create a copy of item;
        let cloned = JSON.parse(JSON.stringify(item));

        if (item.details) {
            cloned.template_id = item.details._id;
        } else {
            cloned.template_id = item._id;
        }

        cloned._id = undefined;
        cloned._rev = undefined
        cloned.type = undefined;

        cloned.control_id = control_mappings[item._id];

        addFormModal({
            fields:formFields,
            data: cloned
        }).then(formData => {
            assessment_model.saveLibraryItem(section, formData).then(() => {
                notifier.success("Item Added");
            });
        }).catch((err) => null);
    }

    function addItem() {
        addFormModal({
            fields: formFields
        }).then(formData => {
            assessment_model.saveLibraryItem(section, formData).then(() => {
                notifier.success("Item Added");
            });
        }).catch((err) => null);
    }

    function saveTemplate(item) {
        addFormModal({
            fields: [{
                name: "comment",
                label: "Change Comment",
                type: "textarea",
                required: true
            }]
        }).then((formData) => {
            let cloned = JSON.parse(JSON.stringify(item));

            delete cloned._id;
            delete cloned._rev;

            library_model.moderation_queue.newItem({
                ...cloned,
                submitter: $session.user.email,
                changeComment: formData.comment
            }).then(() => {
                notifier.success("Item Added to Moderation Queue");
            })
        })
    }


    function manageTags() {
        addFormModal({
            fields: [{name: 'tags', type: "custom", "component": TagEditor, label: "Tags"}],
            data: {
                tags: library_tags.tags
            }
        }).then(data => {
            library_tags.tags = data.tags;
            assessment_model.saveSectionTags(section, library_tags);
        });
    }

    let showComments = {};

    $: load_section_data($assessment, section);
    $: load_library_data(section);
    $: load_mapped_items($assessment, library)

    onMount(() => {
        return () => {
            if (library_unsub) {
                library_unsub()
            }
        }
    })
</script>

<Loader loading={!library}>
    <div class="container is-fluid">
        <SortableTable {sortFields} tableData={library_items}>
            <div slot="filterActions"><button class="button is-success" on:click={addItem}>Add Item</button></div>
            <div slot="rowActions" let:row={item}>
                <div class="buttons is-centered">
                    <button class="button is-danger" on:click={() => deleteItem(item)}>Delete</button>
                    <button class="button is-link" on:click={() => editItem(item)}>Edit</button>
                    <button class="button" on:click={() => saveTemplate(item)}>Save Template</button>
                </div>
                <label class="pointer badge is-pulled-right">
                    <Fa icon={faComment} />
                    {#if ($assessment.library_notes[item.item_id] || []).length}
                        <span>{($assessment.library_notes[item.item_id] || []).length}</span>
                    {/if}
                    <input type="checkbox" class="is-hidden" bind:checked={showComments[item.item_id]}>
                </label>
            </div>

            <svelte:fragment slot="customRow" let:row={item}>
                {#if showComments[item.item_id]}
                    <tr>
                        <td colspan="{libraryFieldData.sortFields.length + 2}">
                            <AssessmentLibraryNotes library_item={item} assessment={$assessment} />
                        </td>
                    </tr>
                {/if}
            </svelte:fragment>

            <div slot="footerActions">
                <div class="buttons">
                    <button class="button is-info" on:click={manageTags}>Manage Tags</button>
                    {#if $assessment.positive_controls.length < 6}
                        <button class="button is-primary is-right" on:click={addItemFromTemplate}>Add From Template</button>
                    {/if}
                </div>
            </div>
        </SortableTable>

        {#if mapped_items}
            <Collapse title="Mapped Items" title_level="2">
                <SortableTable sortFields={libraryFieldData.sortFields} tableData={mapped_items} tableClass="mappedItems">
                    <div slot="rowActions" let:row={item}>
                        <button class="button is-link" on:click={() => addTemplatedItem(item)}>Add Item</button>
                    </div>
                </SortableTable>
            </Collapse>
        {/if}
    </div>


</Loader>


{#if modal}
    <div class="modal is-active">
        <div class="modal-background"></div>
        <div class="modal-card is-fullwidth">
            <header class="modal-card-head">
            <p class="modal-card-title">Create from Template</p>
                <button class="delete" aria-label="close" on:click={modal.cancelAction}></button>
            </header>
            <section class="modal-card-body">
                <SortableTable sortFields={libraryFieldData.sortFields} tableData={library.items.items} tableClass="NISTTable" ORM="true">
                    <div slot="rowActions" let:row={item}>
                        <div class="button is-success" on:click={() => modal.saveAction(item)}>Select Control</div>
                    </div>
                </SortableTable>
            </section>
            <footer class="modal-card-foot has-text-right">
                <button class="button is-pulled-right" on:click={modal.cancelAction}>Close</button>
            </footer>
        </div>
    </div>
{/if}

<style>
    .buttons {
        justify-content: space-between;
    }
</style>